import React, { useState, useEffect } from 'react';
import DataTable from "react-data-table-component";
import * as XLSX from "xlsx";
import { saveAs } from "file-saver";
import "react-data-table-component-extensions/dist/index.css";
import { ToastContainer, toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import AnalyticsApiService from '../../ApiServices/AnalyticsApiService/AnalyticsApiService';
import { useAuth } from '../Auth/AuthContext';
import AddAnalyticsForm from './AddAnalyticsForm';
import { Calendar, User, Globe, Layout, MapPin, Eye, MousePointer, Percent, DollarSign, CreditCard, BarChart3, RefreshCcw, TrendingUp, TrendingDown, MoreVertical } from "lucide-react";
import EditAnalyticsForm from './EditAnalyticsForm';
import MetricCard from '../Cards/MetricCard';
import Filters from '../analytics/Filters';
import { DataStyles } from '../Common/DataTableStyles';

const AnalyticsTable = () => {
  const [loading, setLoading] = useState(false);
  const [editData, setEditData] = useState(null);
  const [modalIsOpen, setModalIsOpen] = useState(false);
  const [editModalIsOpen, setEditModalIsOpen] = useState(false);
  const [selectedPublisher, setSelectedPublisher] = useState("");
  const [selectedWebsite, setSelectedWebsite] = useState("");
  const [dateRange, setDateRange] = useState({ startDate: null, endDate: null });
  const [analytics, setAnalytics] = useState([]);
  const Auth = useAuth();
  const user = Auth.authState.user;
  const role = Auth.authState.roles;
  const admin = role === "Admin";
  const uniquePublishers = [...new Set(analytics.map(entry => entry.publisher))];
  const uniqueWebsites = [...new Set(analytics.map(entry => entry.website))];

  const fetchAnalytics = async () => {
    setLoading(true);
    try {
      const response = admin
        ? await AnalyticsApiService.getAll()
        : await AnalyticsApiService.GetUserAnalytics(user.id);
      setAnalytics(response.data || []);
    } catch (error) {
      toast.error("Failed to fetch analytics.");
    } finally {
      setLoading(false);
    }
  };

  // Refresh analytics data when a new entry is submitted
  const handleAnalyticsSubmit = async () => {
    setLoading(true);
    try {
      await fetchAnalytics(); // re-fetch on submit
    } catch {
      toast.error("Submit failed.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (user && user.id) {
      fetchAnalytics();
    }
  }, [user]);



  const filteredAnalytics = analytics.filter(entry => {
    const entryDate = new Date(entry.date);
    const startDate = dateRange.startDate ? new Date(dateRange.startDate) : null;
    const endDate = dateRange.endDate ? new Date(dateRange.endDate) : null;

    // Normalize all dates to midnight (00:00:00)
    if (startDate) startDate.setHours(0, 0, 0, 0);
    if (endDate) endDate.setHours(23, 59, 59, 999); // Extend end date to the end of the day
    entryDate.setHours(0, 0, 0, 0);

    const isWithinRange =
      (!startDate || entryDate >= startDate) &&
      (!endDate || entryDate <= endDate);

    return (
      (selectedPublisher ? entry.publisher === selectedPublisher : true) &&
      (selectedWebsite ? entry.website === selectedWebsite : true) &&
      isWithinRange
    );
  });
  const handleResetFilters = () => {
    setTimeout(() => {
      setSelectedPublisher("");
      setSelectedWebsite("");
      setDateRange({ startDate: null, endDate: null });
    }, 800); // Simulate a delay
  };

  // Modify filter change functions
  const handleFilterChange = (setter, value) => {
    setTimeout(() => {
      setter(value);
    }, 800);
  };

  const handleDelete = async (id) => {
    if (window.confirm("Are you sure you want to delete this entry?")) {
      setLoading(true);
      try {
        await AnalyticsApiService.deleteAnalytics(id);
        toast.success("Deleted successfully!");
        fetchAnalytics(); // refresh data after delete
      } catch (error) {
        toast.error("Error deleting analytics");
      } finally {
        setLoading(false);
      }
    }
  };

  const handleEdit = (row) => {
    setEditData(row);
    setEditModalIsOpen(true);
  };
  const columns = [
    { name: "No", selector: (_, index) => index + 1, width: "50px", sortable: true },
    { name: "Date", selector: row => new Date(row.date).toISOString().split("T")[0], sortable: true, icon: <Calendar size={16} /> },
    { name: "Publisher", selector: row => row.publisher, width: "150px", sortable: true, icon: <User size={16} /> },
    { name: "Site", selector: row => row.website, sortable: true, width: "180px", icon: <Globe size={16} /> },
    { name: "Adunits", selector: row => row.adUnit, sortable: true, width: "190px", icon: <Layout size={16} /> },
    { name: "Country", selector: row => row.country, sortable: true, width: "120px", icon: <MapPin size={16} /> },
    { name: "Impressions", selector: row => row.adExchangeImpressions, sortable: true, icon: <Eye size={16} /> },
    { name: "Clicks", selector: row => row.adExchangeClicks, sortable: true, icon: <MousePointer size={16} /> },
    { name: "CTR (%)", selector: row => row.adExchangeCTR, sortable: true, icon: <Percent size={16} /> },
    { name: "Revenue ($)", selector: row => row.adExchangeRevenue, sortable: true, icon: <DollarSign size={16} /> },
    { name: "Payout", selector: row => row.adExchangePayout, sortable: true, icon: <CreditCard size={16} /> },
    { name: "eCPM ($)", selector: row => row.adExchangeAvgECPM, sortable: true, icon: <BarChart3 size={16} /> },
    { name: "Requests", selector: row => row.adExchangeAdRequests, sortable: true, icon: <RefreshCcw size={16} /> },
    { name: "Match Rate (%)", selector: row => row.adExchangeMatchRate, sortable: true, icon: <TrendingUp size={16} /> },
    { name: "Fill Rate (%)", selector: row => row.adExchangeFillRate, sortable: true, icon: <TrendingDown size={16} /> }
  ];
  // Conditionally add "Actions" column if the user is an admin
  if (admin) {
    columns.push({
      name: "Actions",
      cell: (row) => (
        <div className="flex gap-2">
          <button onClick={() => handleEdit(row)} className="text-blue-500 hover:underline">
            Edit
          </button>
          <button onClick={() => handleDelete(row.id)} className="text-red-500 hover:underline">
            Delete
          </button>
        </div>
      ),
      ignoreRowClick: true,
      allowOverflow: true,
      button: true,
    });
  }

  // ✅ Calculate Totals for Footer
  const totalImpressions = filteredAnalytics.reduce((sum, row) => sum + (row.adExchangeImpressions || 0), 0);
  const totalClicks = filteredAnalytics.reduce((sum, row) => sum + (row.adExchangeClicks || 0), 0);
  const totalCTR = (totalImpressions > 0) ? ((totalClicks / totalImpressions) * 100).toFixed(2) : 0;
  const totalRevenue = filteredAnalytics.reduce((sum, row) => sum + (row.adExchangeRevenue || 0), 0).toFixed(2);
  const totalPayout = filteredAnalytics.reduce((sum, row) => sum + (row.adExchangePayout || 0), 0).toFixed(2);
  const totalRequests = filteredAnalytics.reduce((sum, row) => sum + (row.adExchangeAdRequests || 0), 0);
  const totalFillRate = (filteredAnalytics.length > 0) ? (filteredAnalytics.reduce((sum, row) => sum + (row.adExchangeFillRate || 0), 0) / filteredAnalytics.length).toFixed(2) : 0;
  var sumObj = {
    totalImpressions, totalClicks, totalCTR, totalRevenue, totalPayout, totalRequests, totalFillRate
  }
  const exportToExcel = (data) => {
    const worksheet = XLSX.utils.json_to_sheet(data);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, "Analytics Data");

    // Convert to buffer and create a Blob
    const excelBuffer = XLSX.write(workbook, { bookType: "xlsx", type: "array" });
    const dataBlob = new Blob([excelBuffer], { type: "application/octet-stream" });

    // Save file
    saveAs(dataBlob, "Analytics_Data.xlsx");
  };

  const handleUpdateAnalytics = async (updatedData) => {
    setLoading(true);
    try {
      const id = updatedData.id;
      const payload = { ...updatedData };
      delete payload.id;
      payload.date = updatedData.date.toISOString();

      await AnalyticsApiService.updateAnalytics(id, payload);
      toast.success("Analytics updated successfully!");
      fetchAnalytics(); // refresh data
    } catch (error) {
      toast.error("Update failed.");
    } finally {
      setLoading(false);
    }
  };



  
  const calculateMetrics = () => {
    const totals = {
      ctr: filteredAnalytics.reduce((sum, row) => sum + (row.adExchangeCTR || 0), 0).toFixed(2),
    ecpm: filteredAnalytics.reduce((sum, row) => sum + (row.adExchangeAvgECPM || 0), 0).toFixed(2),
      impressions: filteredAnalytics.reduce((sum, row) => sum + (row.adExchangeImpressions || 0), 0),
      clicks: filteredAnalytics.reduce((sum, row) => sum + (row.adExchangeClicks || 0), 0),
      revenue: filteredAnalytics.reduce((sum, row) => sum + (row.adExchangeRevenue || 0), 0).toFixed(2),
      payout: filteredAnalytics.reduce((sum, row) => sum + (row.adExchangePayout || 0), 0).toFixed(2),
      fillRate: (filteredAnalytics.length > 0 ?
        (filteredAnalytics.reduce((sum, row) => sum + (row.adExchangeFillRate || 0), 0) / filteredAnalytics.length).toFixed(2) : 0),
      adrequest: (filteredAnalytics.length > 0 ?
        (filteredAnalytics.reduce((sum, row) => sum + (row.adExchangeAdRequests || 0), 0) / filteredAnalytics.length).toFixed(2) : 0),
    };

    // Calculate trends (simplified - you would compare with previous period data)
    const trends = {
      impressions: 'up',
      clicks: 'up',
      revenue: 'up',
      ctr: 'up',
      ecpm: 'up',
      payout: 'up',
      fillRate: 'up',
      adrequest: 'up'
    };

    return { totals, trends };
  };

  const { totals, trends } = calculateMetrics();
  const metricCardsData = [
    {
      title: "Total Impressions",
      value: totals.impressions.toLocaleString(),
      icon: <Eye size={20} />,
      trend: trends.impressions,
      change: "+12.5%",
      color: "white",
    },
    {
      title: "Total Clicks",
      value: totals.clicks.toLocaleString(),
      icon: <MousePointer size={20} />,
      trend: trends.clicks,
      change: "+8.3%",
      color: "blue",
    },
    {
      title: "Total Revenue",
      value: `$${totals.revenue}`,
      icon: <DollarSign size={20} />,
      trend: trends.revenue,
      change: "+15.2%",
      color: "purple",
    },
    {
      title: "Avg CTR",
      value: `${totals.ctr}%`,
      icon: <Percent size={20} />,
      trend: trends.ctr,
      change: "+1.2%",
      color: "orange",
    },
    {
      title: "Avg eCPM",
      value: `$${totals.ecpm}`,
      icon: <BarChart3 size={20} />,
      trend: trends.ecpm,
      change: "+2.7%",
      color: "orange",
    },
    {
      title: "Total Payout",
      value: `$${totals.payout}`,
      icon: <CreditCard size={20} />,
      trend: trends.payout,
      change: "+10.1%",
      color: "purple",
    },
    {
      title: "Avg Fill Rate",
      value: `${totals.fillRate}%`,
      icon: <TrendingUp size={20} />,
      trend: trends.fillRate,
      change: "+0.8%",
      color: "blue",
    },
    {
      title: "Total Ad Requests",
      value: `${totals.adrequest}%`,
      icon: <TrendingUp size={20} />,
      trend: trends.adrequest,
      change: "+0.8%",
      color: "white",
    },
  ];

  return (

    <div className="p-6 bg-gray-100 min-h-screen">
      <ToastContainer />
      {loading && (
        <div className="fixed inset-0 flex items-center justify-center bg-white/80 z-50">
          <div className="animate-spin rounded-full h-16 w-16 border-b-2 border-gray-800"></div>
        </div>
      )}
      {/* Summary Cards Section */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4 mb-6">
        {metricCardsData.map((card, index) => (
          <MetricCard
            key={index}
            title={card.title}
            value={card.value}
            icon={card.icon}
            trend={card.trend}
            change={card.change}
            color={card.color}
          />
        ))}
      </div>

      <Filters
        selectedPublisher={selectedPublisher}
        selectedWebsite={selectedWebsite}
        dateRange={dateRange}
        uniquePublishers={uniquePublishers}
        uniqueWebsites={uniqueWebsites}
        handleFilterChange={handleFilterChange}
        handleResetFilters={handleResetFilters}
        setSelectedPublisher={setSelectedPublisher}
        setSelectedWebsite={setSelectedWebsite}
        setDateRange={setDateRange}
        setModalIsOpen={setModalIsOpen}
        admin={admin}
      />


      <div className="bg-white shadow-lg rounded-lg p-6">
        <button className='px-4 py-2 bg-blue-600 text-white rounded-lg shadow-md hover:bg-blue-700 transition duration-200'
          onClick={() => exportToExcel(filteredAnalytics)}>Export to excel</button>

<DataTable
  title="Analytics Data"
  pagination
  highlightOnHover
  striped
  responsive
  className="min-w-full bg-white border-collapse"
  customStyles={DataStyles}
  columns={columns}
  data={filteredAnalytics}
  paginationPerPage={20} // ✅ default page size
  paginationRowsPerPageOptions={[20, 50, 100]} // ✅ max selectable 100
/>

      </div>
      {modalIsOpen && (
        <AddAnalyticsForm
          isOpen={modalIsOpen}
          onRequestClose={() => setModalIsOpen(false)}
          onSubmit={handleAnalyticsSubmit}
        />
      )}

      {editModalIsOpen && (
        <EditAnalyticsForm
          data={editData}
          onClose={() => setEditModalIsOpen(false)}
          onSubmit={handleUpdateAnalytics}
        />
      )}
    </div>
  );
};

export default AnalyticsTable;
